import React, { useEffect } from 'react';

interface SnackBarProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

/**
 * @fileoverview Componente SnackBar para notificaciones
 * @description Muestra notificaciones temporales con diferentes tipos
 */

const SnackBar: React.FC<SnackBarProps> = ({ 
  isOpen, 
  onClose, 
  message, 
  type = 'success',
  duration = 4000,
  position = 'top-right'
}) => {
  useEffect(() => {
    if (isOpen && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, duration, onClose]);

  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          backgroundColor: '#d4edda',
          color: '#155724',
          borderColor: '#c3e6cb',
          icon: ''
        };
      case 'error':
        return {
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderColor: '#f5c6cb',
          icon: ''
        };
      case 'warning':
        return {
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderColor: '#ffeaa7',
          icon: ''        };
      case 'info':
        return {
          backgroundColor: '#d1ecf1',
          color: '#0c5460',
          borderColor: '#bee5eb',
          icon: ''
        };
      default:
        return {
          backgroundColor: '#d4edda',
          color: '#155724',
          borderColor: '#c3e6cb',
          icon: ''
        };
    }
  };

  const getPositionStyles = () => {
    const baseStyles: React.CSSProperties = {
      position: 'fixed',
      zIndex: 1001,
      maxWidth: '400px',
      minWidth: '300px',
      borderRadius: '8px',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
      display: 'flex',
      alignItems: 'center',
      padding: '20px',
      margin: '20px',
      transition: 'all 0.3s ease',
      transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
    };

    switch (position) {
      case 'top-right':
        return { ...baseStyles, top: 0, right: 0 };
      case 'top-left':
        return { ...baseStyles, top: 0, left: 0 };
      case 'bottom-right':
        return { ...baseStyles, bottom: 0, right: 0 };
      case 'bottom-left':
        return { ...baseStyles, bottom: 0, left: 0 };
      case 'top-center':
        return { ...baseStyles, top: 0, left: '50%', transform: 'translateX(-50%)' };
      case 'bottom-center':
        return { ...baseStyles, bottom: 0, left: '50%', transform: 'translateX(-50%)' };
      default:
        return { ...baseStyles, top: 0, right: 0 };
    }
  };

  const typeStyles = getTypeStyles();
  const positionStyles = getPositionStyles();

  return (
    <div
      className="snackbar"
      style={{
        ...positionStyles,
        backgroundColor: typeStyles.backgroundColor,
        color: typeStyles.color,
        border: `1px solid ${typeStyles.borderColor}`,
      }}
    >
      <div 
        className="snackbar-icon"
        style={{
          fontSize: '24px',
          marginRight: '16px',
          fontWeight: 'bold',
          minWidth: '24px',
          textAlign: 'center',
        }}
      >
        {typeStyles.icon}
      </div>
      
      <div 
        className="snackbar-message"
        style={{
          flex: 1,
          fontSize: '16px',
          fontWeight: '600',
          lineHeight: '1.4',
        }}
      >
        {message}
      </div>
      
      <button
        onClick={onClose}
        className="snackbar-close"
        style={{
          background: 'none',
          border: 'none',
          color: typeStyles.color,
          cursor: 'pointer',
          fontSize: '20px',
          marginLeft: '16px',
          padding: '6px',
          borderRadius: '6px',
          opacity: 0.8,
          transition: 'all 0.2s ease',
          minWidth: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.8';
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        ×
      </button>
    </div>
  );
};

export default SnackBar;
